import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/login.css';
import { buildApiUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { logError } from '../../utils/logger';

const initialFormState = {
  email: '',
  password: '',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const { setSession } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const validationErrors = {};

    if (!form.email.trim()) {
      validationErrors.email = 'El correo es obligatorio';
    } else if (!emailRegex.test(form.email.trim())) {
      validationErrors.email = 'Ingresa un correo válido';
    }

    if (!form.password) {
      validationErrors.password = 'La contraseña es obligatoria';
    }

    return validationErrors;
  }, [form.email, form.password]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setMessage(null);

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});
      setLoading(true);

      try {
        const response = await fetch(buildApiUrl('/api/auth/login'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: form.email.trim(),
            password: form.password,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setSession({ token: data.token, user: data.user });
          window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: data.user }));
          setMessage({ type: 'success', text: '¡Inicio de sesión exitoso!' });
          notify({ message: 'Sesión iniciada correctamente.', type: 'success' });

          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1200);
        } else {
          const errorText = data?.error || 'Error al iniciar sesión. Verifica tus credenciales.';
          setMessage({ type: 'error', text: errorText });
        }
      } catch (err) {
        logError('[LoginPage] Error iniciando sesión', err);
        setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
      } finally {
        setLoading(false);
      }
    },
    [form.email, form.password, navigate, notify, setSession, validate],
  );

  const handleForgotPassword = useCallback(
    (event) => {
      event.preventDefault();
      navigate('/forgot-password');
    },
    [navigate],
  );

  return (
    <div className="login-page" role="main">
      <div className="login-container">
        <div className="login-form-section">
          <div className="login-form-header">
            <h2>Iniciar Sesión</h2>
            <p>Ingresa tus credenciales para acceder a tu cuenta</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {message ? (
              <div
                className={`auth-message ${message.type === 'error' ? 'error' : 'success'}`}
                role="alert"
              >
                {message.text}
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="loginEmail" className="form-label">
                <i className="fas fa-envelope" aria-hidden="true" />
                Correo Electrónico
              </label>
              <input
                type="email"
                id="loginEmail"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`form-input${errors.email ? ' error' : ''}`}
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
              {errors.email ? <div className="form-error">{errors.email}</div> : null}
            </div>

            <div className="form-group">
              <label htmlFor="loginPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Contraseña
              </label>
              <input
                type="password"
                id="loginPassword"
                name="password"
                value={form.password}
                onChange={handleChange}
                className={`form-input${errors.password ? ' error' : ''}`}
                placeholder="Tu contraseña"
                autoComplete="current-password"
                required
              />
              {errors.password ? <div className="form-error">{errors.password}</div> : null}
            </div>

            <div className="login-options">
              <button 
                type="button" 
                onClick={handleForgotPassword}
                className="forgot-password-btn"
                aria-label="Recuperar contraseña"
              >
                <i className="fas fa-key" aria-hidden="true"></i>
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <button type="submit" className="login-submit" disabled={loading || hasErrors}>
              <span
                className="auth-loading"
                aria-hidden="true"
                style={{ display: loading ? 'inline-block' : 'none' }}
              />
              <i className="fas fa-sign-in-alt" aria-hidden="true" />
              Iniciar Sesión
            </button>
          </form>

          <div className="register-link">
            <p>¿No tienes una cuenta?</p>
            <Link to="/register">¡Regístrate aquí!</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;





